import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import axiosClient from "../api/axiosClient";
import Swal from "sweetalert2";
import usePreventBack from "../hooks/usePreventBack";
import codematrixLogo from "../assets/Logo/codematrixLogo.png";
import {
  Play,
  Send,
  CheckCircle,
  Maximize2,
  Minimize2,
  ArrowDown,
  ArrowRight,
  Radio,
} from "lucide-react";
import { toast } from "react-hot-toast";
import Editor from "@monaco-editor/react";
//import s from "./script8";
import s from "../script/quizS.js";

export default function SplitQuizPage() {
  usePreventBack();
  // ✅ QUESTION SCROLL REF
  const questionRef = React.useRef(null);
  const [isRestored, setIsRestored] = useState(false);
  const [questionStatus, setQuestionStatus] = useState({});

  // Automated test-case execution
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testMode, setTestMode] = useState(null);
  const testResultsRef = useRef(null);

  const testOutputStyle = {
    margin: "5px 0 0",
    padding: 10,
    borderRadius: 6,
    background: "#020617",
    color: "#e2e8f0",
    whiteSpace: "pre-wrap",
    overflowX: "auto",
    overflowWrap: "anywhere",
    fontFamily: "monospace",
    fontSize: 12,
  };

  const runAutomatedTests = async (mode) => {
    if (!code?.trim()) {
      toast.error("Please write some code first!");
      return;
    }

    if (!selectedLang?.id) {
      toast.error("Please select a programming language!");
      return;
    }

    // Use the question ID expected by your backend.
    const codingQuestionId = Number(
      currentQuestion?.questionId ?? currentQuestion?.id,
    );

    if (!Number.isInteger(codingQuestionId) || codingQuestionId <= 0) {
      toast.error("Coding question ID is missing or invalid.");
      return;
    }

    try {
      setIsTesting(true);
      setTestResults(null);
      setTestMode(mode);
      setOutput("");

      const token = localStorage.getItem("token");

      const payload = {
        codingQuestionId,
        sourceCode: code,
        languageId: Number(selectedLang.id),
        mode, // RUN_SAMPLE or SUBMIT
      };

      const response = await axiosClient.post(
        "/auth/api/users/coding/run-tests",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Supports either a direct response or an ApiResponse { data: ... }.
      const result = response.data?.data ?? response.data;
      console.log("result", result);

      setTestResults(result);

      const passed = Number(result.passedTests ?? 0);
      const total = Number(result.totalTests ?? result.results?.length ?? 0);

      if (total > 0 && passed === total) {
        toast.success(`All ${total} test cases passed!`);
      } else {
        toast.error(`${passed} of ${total} test cases passed.`);
      }

      setTimeout(() => {
        testResultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (error) {
      console.error("Automated test execution failed:", error);

      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Unable to execute test cases. Please try again.";

      setTestResults({
        error: message,
        totalTests: 0,
        passedTests: 0,
        failedTests: 0,
        results: [],
      });

      toast.error(message);

      setTimeout(() => {
        testResultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } finally {
      setIsTesting(false);
    }
  };

  // 🔥 SABSE PEHLE
  // 🔥 SABSE PEHLE
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("reportUserId") ||
    "";

  const courseId = localStorage.getItem("selectedCourse") || "default";

  // ✅ MOVE THIS HERE
  const mode = localStorage.getItem("testMode");
  const isFilter = mode === "CUSTOM";

  const [codingSubmitted, setCodingSubmitted] = useState({});

  useEffect(() => {
    if (mode !== "CUSTOM") return;

    const saved =
      JSON.parse(
        localStorage.getItem(`codingSubmitted_${userId}_${courseId}`),
      ) || {};

    setCodingSubmitted(saved);
  }, [userId, courseId, mode]);

  useEffect(() => {
    if (!isFilter) return; // 🔥 ONLY FILTER

    const savedOutputs = JSON.parse(
      localStorage.getItem(`outputs_${userId}_${courseId}`) || "{}",
    );

    setOutputs(savedOutputs);
  }, [userId, courseId, isFilter]);

  useEffect(() => {
    if (!isFilter) return;

    const savedAnswers =
      JSON.parse(localStorage.getItem(`answers_${userId}_${courseId}`)) || {};

    setAnswers(savedAnswers);
  }, [userId, courseId, isFilter]);

  useEffect(() => {
    if (!isFilter) return;

    const savedStatus = JSON.parse(
      localStorage.getItem(`status_${userId}_${courseId}`) || "{}",
    );

    setQuestionStatus(savedStatus);
  }, [userId, courseId, isFilter]);

  const fullResetQuiz = () => {
    console.log("🧹 FULL QUIZ RESET");

    // ❌ remove all saved data
    localStorage.removeItem(`status_${userId}_${courseId}`);
    localStorage.removeItem(`answers_${userId}_${courseId}`);
    localStorage.removeItem(`codes_${userId}_${courseId}`);
    localStorage.removeItem(`outputs_${userId}_${courseId}`);
    localStorage.removeItem(`codingStats_${userId}_${courseId}`);
    localStorage.removeItem(`mcqStats_${userId}_${courseId}`);
    localStorage.removeItem(`activeTest_${userId}`);

    // ❌ remove resume flags
    localStorage.removeItem("startFreshTest");

    // ✅ UI reset
    setAnswers({});
    setCodes({});
    setOutputs({});
    setCodingSubmitted({}); // 🔥 ADD
    setOutput(""); // 🔥 ADD
    setQuestionStatus({});
    setCurrentIndex(0);

    setCodingStats({ attempted: 0, skipped: 0 });
    setMcqStats({ attempted: 0, skipped: 0 });
  };

  // POST /auth/saveEvidence — records WHY the test ended (e.g. TAB_SWITCH)
  const sendEvidence = async (eliminationReason = null) => {
    const testName =
      localStorage.getItem(`testName_${userId}`) || questions[0]?.testName;

    if (!userId || !testName) {
      console.log("❌ No userId/testName → skipping evidence");
      return;
    }

    const payload = {
      userId: String(userId),
      testName: String(testName),
      elimination: eliminationReason ? String(eliminationReason) : null,
      screenCapture: [],
      cameraCapture: [],
    };

    try {
      const res = await axiosClient.post("/auth/saveEvidence", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      console.log("EVIDENCE SAVED:", res.data);
    } catch (err) {
      console.log("EVIDENCE API ERROR:", err?.response?.data || err.message);
    }
  };

  // ✅ USER DROPDOWN STATE
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // ✅ USER INFO
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const userName = storedUser.name || "Guest User";
  const userEmail = storedUser.email || "guest@mail.com";

  // ✅ SAFE ARRAY TEXT HELPER (constraints, hints, examples)
  const safeArrayText = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((e) => {
        if (typeof e === "string") return e.trim();
        if (typeof e === "number") return String(e);
        if (typeof e === "object") return JSON.stringify(e, null, 2);
        return "";
      })
      .filter(Boolean);
  };

  // --------------helper function-----------------
  const parseExamples = (examplesJson) => {
    if (!examplesJson) return [];

    try {
      return JSON.parse(examplesJson);
    } catch {
      return [];
    }
  };
  // --------------helper function-----------------

  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // ❌ savedState yahan use hi mat karo

  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);

  const [codes, setCodes] = useState({});
  const [code, setCode] = useState("");

  const [selectedLang, setSelectedLang] = useState(null);

  const [outputs, setOutputs] = useState({});
  const [output, setOutput] = useState("");

  const [terminalMode, setTerminalMode] = useState("bottom");
  const [languages, setLanguages] = useState([]);

  const [stdin, setStdin] = useState("");
  const [timeTaken, setTimeTaken] = useState(0);

  const currentQuestion = questions[currentIndex] || null;

  const [langOpen, setLangOpen] = useState(false);
  const [editorFontSize, setEditorFontSize] = useState(14);

  const [isRunning, setIsRunning] = useState(false);
  const outputRef = useRef(null);

  const [moduleName, setModuleName] = useState(
    localStorage.getItem("selectedCourse") || "",
  );

  const resetAllForNewUser = () => {
    // 🔥 CORE DATA

    // 🔥 USER + COURSE SPECIFIC CLEAN
    localStorage.removeItem(`activeTest_${userId}`);
    localStorage.removeItem(`lastTest_${userId}_${courseId}`);

    // 🔥 STATE RESET
    setAnswers({});
    setCodes({});
    setOutputs({});
    setCurrentIndex(0);
    setCode("");
    setOutput("");
    setSelectedLang(null);

    setCodingStats({ attempted: 0, skipped: 0 });
    setMcqStats({ attempted: 0, skipped: 0 });

    console.log("🧹 FULL RESET DONE FOR NEW USER");
  };

  const resetTerminal = () => {
    setStdin(""); // ✅ sirf input clear ho
  };

  const LANGUAGE_MAP = {
    javascript: 63,
    python: 71,
    java: 62,
    c: 50,
    "c++": 54,
  };
  const normalizeLang = (lang) => {
    if (!lang) return "javascript";

    const l = lang.toLowerCase();

    if (l.includes("python")) return "python";
    if (l.includes("javascript")) return "javascript";
    if (l.includes("java")) return "java";
    if (l.includes("c++")) return "c++";
    if (l.includes("c")) return "c";

    return "javascript";
  };

  // ⏱ Timer (Runs & visible update)

  useEffect(() => {
    if (!questions.length) return;

    setTimeTaken(0); // reset when question changes

    const interval = setInterval(() => {
      setTimeTaken((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentIndex, questions.length]);

  // ✅ Load saved code when question changes (Prev / Next / Refresh)
  useEffect(() => {
    if (mode === "CUSTOM") {
      setCode(codes[currentIndex] || "");
    } else {
      setCode("");
    }
    prevCodeRef.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // ✅ Load saved output when question changes (Prev / Next / Refresh)
  // useEffect(() => {
  //   if (outputs.hasOwnProperty(currentIndex)) {
  //     setOutput(outputs[currentIndex]);
  //   } else {
  //     setOutput("");
  //   }
  // }, [currentIndex, outputs]);

  useEffect(() => {
    if (outputs.hasOwnProperty(currentIndex)) {
      setOutput(outputs[currentIndex]);
    } else {
      setOutput(""); // 🔥 ADD THIS LINE
    }
  }, [currentIndex, outputs]);

  useEffect(() => {
    if (!moduleName) return;

    const fetchQuizData = async () => {
      try {
        setLoading(true);

        const res = await axiosClient.get("/auth/get-questions", {
          params: { module: moduleName },
        });

        const questionList = Array.isArray(res.data?.data) ? res.data.data : [];

        setQuestions(questionList);

        if (!questionList.length) return;

        const startFresh = localStorage.getItem("startFreshTest");

        // ✅ STEP 1: CLEAR FIRST
        if (startFresh === "true") {
          console.log("🆕 Fresh Test Mode");

          localStorage.removeItem(`status_${userId}_${courseId}`);
          localStorage.removeItem(`codingStats_${userId}_${courseId}`);
          localStorage.removeItem(`mcqStats_${userId}_${courseId}`);
          localStorage.removeItem(`answers_${userId}_${courseId}`);
          localStorage.removeItem(`codes_${userId}_${courseId}`);
          localStorage.removeItem(`outputs_${userId}_${courseId}`);
          localStorage.removeItem(`activeTest_${userId}`);
          localStorage.removeItem(`testName_${userId}`);
          localStorage.removeItem(`codingSubmitted_${userId}_${courseId}`);
          localStorage.removeItem("startFreshTest");

          // reset states
          setQuestionStatus({});
          setAnswers({});
          setCodes({});
          setOutputs({});
          setOutput("");
          setCurrentIndex(0);
        }

        // ✅ STEP 2: NOW SET NEW TEST
        const testName = questionList[0].testName;

        localStorage.setItem(`testName_${userId}`, testName);

        localStorage.setItem(
          `activeTest_${userId}`,
          JSON.stringify({
            module: courseId,
            testName: testName,
            type: "CUSTOM",
            inProgress: true,
          }),
        );

        setCurrentIndex(0);
        console.log(questionList);
      } catch (err) {
        alert(err.response?.data?.message)
        toast.error(err.response?.data?.message || "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [moduleName]);

  // ------------------------------------------

  // ---------------------------keyrhythm---------------

  const hasHiddenRef = useRef(false);
  const quizViolationRef = useRef(false);

  const questionStartTimeRef = useRef(Date.now());
  const firstKeyPressTimeRef = useRef(null);
  const prevCodeRef = useRef("");

  const [tabSwitches, setTabSwitches] = useState(0);
  const [backspaceCount, setBackspaceCount] = useState(0);
  const [totalKeystrokes, setTotalKeystrokes] = useState(0);
  const [initialThinkingTimeMs, setInitialThinkingTimeMs] = useState(0);

  // Always-fresh ref so the tab-switch listener (registered once) can
  // call the *current* submit/finish logic instead of stale closures.
  const eliminationHandlerRef = useRef(() => {});

  useEffect(() => {
    eliminationHandlerRef.current = async () => {
      const idx = currentIndex;
      const qNow = currentQuestion;
      const optionsNow = qNow
        ? [qNow.optionA, qNow.optionB, qNow.optionC, qNow.optionD].filter(
            Boolean,
          )
        : [];
      const isMCQNow =
        qNow?.formate === "MCQ" ||
        qNow?.format === "MCQ" ||
        optionsNow.length > 0;

      // 1) save the last answer
      try {
        const userAnswer = isMCQNow ? answers?.[idx] : code?.trim();
        await submitCurrentAnswerDirect(userAnswer || null);
      } catch (err) {
        console.log("⚠️ Could not save last answer before elimination:", err);
      }

      // 2) record WHY the test ended (must go BEFORE finish-test)
      try {
        await sendEvidence("TAB_SWITCH");
      } catch (err) {
        console.log("⚠️ Could not save evidence:", err);
      }

      // 3) finish-test
      try {
        await submitAllAnswer();
      } catch (err) {
        console.log("⚠️ finish-test failed after elimination:", err);
        fullResetQuiz();
        navigate("/front");
      }
    };
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      // User left the quiz tab
      if (document.hidden && !hasHiddenRef.current) {
        hasHiddenRef.current = true;

        console.log("🚨 TAB SWITCH DETECTED");

        // Prevent running multiple times
        if (quizViolationRef.current) return;

        quizViolationRef.current = true;

        // Stop camera if running
        if (window.stopCamera) {
          window.stopCamera();
        }

        // Mark quiz as eliminated
        localStorage.setItem("quiz_eliminated", "true");

        Swal.fire({
          icon: "error",
          title: "Test Terminated",
          text: "You switched to another tab or window. The test has been terminated.",
          confirmButtonText: "Go to Dashboard",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          eliminationHandlerRef.current();
        });
      }

      // User came back
      if (!document.hidden) {
        hasHiddenRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);

  useEffect(() => {
    questionStartTimeRef.current = Date.now();
    firstKeyPressTimeRef.current = null;

    setTabSwitches(0);
    setBackspaceCount(0);
    setTotalKeystrokes(0);
    setInitialThinkingTimeMs(0);

    hasHiddenRef.current = false;
  }, [currentIndex]);

  // ----------------------------feedback page-------------------------------

  // useEffect(() => {
  //   const userId = localStorage.getItem("userId");
  //   const feedbackDone =
  //     localStorage.getItem(`feedback_done_${userId}`) === "true";

  //   if (!feedbackDone) {
  //     localStorage.setItem("fromQuiz", "true");
  //     navigate("/feedback");
  //   }
  // }, []);

  // const [checkingProfile, setCheckingProfile] = useState(true);

  // useEffect(() => {
  //   if (!localStorage.getItem("token")) return;

  //   const checkProfile = async () => {
  //     try {
  //       const res = await axiosClient.get("/auth/dashboard");
  //       const profile = res.data?.data?.userCareerProfile;

  //       if (!profile) {
  //         localStorage.setItem("fromQuiz", "true");
  //         navigate("/feedback");
  //         return;
  //       }

  //       // ✅ PROFILE EXISTS → SKIP
  //       const userId = localStorage.getItem("userId");
  //       localStorage.setItem(`feedback_done_${userId}`, "true");

  //     } catch (err) {
  //       console.log("Feedback check failed");
  //     } finally {
  //       setCheckingProfile(false); // 🔥 IMPORTANT
  //     }
  //   };

  //   checkProfile();
  // }, []);

  // ----------------------------feedback page-------------------------------

  // ----------------------submit answer----------------
  const safeText = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  };

  const submitCurrentAnswer = async () => {
    const userAnswer = isMCQ ? answers[currentIndex] : code?.trim();

    if (!userAnswer) {
      toast.error("Please select answer or write code!");
      return;
    }

    try {
      const realId = currentQuestion?.questionId;

      const testName =
        localStorage.getItem(`testName_${userId}`) || questions[0]?.testName;
      const payload = {
        userId: userId,
        testName: String(testName),
        questionId: Number(realId),
        questionFormat: isMCQ ? "MCQ" : "CODING",
        userAnswer: String(userAnswer),
        totalTimeSeconds: Number(timeTaken || 0),
        ...(!isMCQ && {
          selectedLanguage: selectedLang?.name || selectedLang?.label || "",
        }),
      };

      console.log("Selected language:", selectedLang);
      console.log("Language being saved:", payload.selectedLanguage);

      // 🔥 Behavioral data ONLY for coding
      if (!isMCQ) {
        payload.tabSwitches = tabSwitches;
        payload.backspaceCount = backspaceCount;
        payload.totalKeystrokes = totalKeystrokes;
        payload.initialThinkingTimeMs = initialThinkingTimeMs;
      }
      console.log("📤 FINAL SUBMIT PAYLOAD:", payload);

      const res = await axiosClient.post(
        "/auth/save-question-progress",
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const finalMsg = `Submit Answer Successfully`;
      setQuestionStatus((prev) => {
        const updated = { ...prev };

        // overwrite old status
        updated[currentIndex] = "DONE";

        localStorage.setItem(
          `status_${userId}_${courseId}`,
          JSON.stringify(updated),
        );

        return updated;
      });

      // const msg = "✅ Submit Successfully";

      // if (isFilter) {
      //   setOutputs((prev) => {
      //     const updated = {
      //       ...prev,
      //       [currentIndex]: msg,
      //     };

      //     localStorage.setItem(
      //       `outputs_${userId}_${courseId}`,
      //       JSON.stringify(updated),
      //     );

      //     return updated;
      //   });
      // }

      // setOutput(msg);

      // optional auto hide

      Swal.fire({
        icon: "success",
        title: "Code Saved!",
        text: "Your code has been submitted successfully.",
        confirmButtonText: "OK",
        confirmButtonColor: "#6366f1",
        timer: 2500,
        timerProgressBar: true,
      });

      // 🔥 mark as submitted
      setCodingSubmitted((prev) => {
        const updated = {
          ...prev,
          [currentIndex]: true,
        };

        localStorage.setItem(
          `codingSubmitted_${userId}_${courseId}`,
          JSON.stringify(updated),
        );

        return updated;
      });

      // ✅ --------------PER QUESTION SAVE
      // setOutputs((prev) => {
      //   const updated = {
      //     ...prev,
      //     [currentIndex]: finalMsg,
      //   };

      //   localStorage.setItem(
      //     `outputs_${userId}_${courseId}`,
      //     JSON.stringify(updated),
      //   );

      //   return updated;
      // });

      // ✅ --------------Behavioral reset ONLY for coding
      if (!isMCQ) {
        setTabSwitches(0);
        setBackspaceCount(0);
        setTotalKeystrokes(0);
        setInitialThinkingTimeMs(0);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || "Submit failed";

      setOutput("❌ " + errMsg);

      toast.error(errMsg);
    }
  };

  // ------------------------------------------------------------------

  // 🔥 ----------------NEW DIRECT MCQ SUBMIT FUNCTION
  const submitCurrentAnswerDirect = async (selectedLetter) => {
    // already answered and nothing new → don't overwrite the saved answer
    if (!selectedLetter && questionStatus[currentIndex] === "DONE") return;

    try {
      const realId =
        currentQuestion?.questionId ||
        currentQuestion?.id ||
        currentQuestion?.question_id ||
        currentQuestion?._id;

      const testName =
        localStorage.getItem(`testName_${userId}`) || questions[0]?.testName;

      const payload = {
        userId: userId,
        testName: String(testName),
        questionId: Number(realId),
        questionFormat: isMCQ ? "MCQ" : "CODING",
        userAnswer: selectedLetter ? String(selectedLetter) : null,
        totalTimeSeconds: Number(timeTaken || 0),
      };

      await axiosClient.post("/auth/save-question-progress", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setQuestionStatus((prev) => {
        // never downgrade DONE -> SKIP
        if (!selectedLetter && prev[currentIndex] === "DONE") return prev;

        const updated = {
          ...prev,
          [currentIndex]: selectedLetter ? "DONE" : "SKIP",
        };

        localStorage.setItem(
          `status_${userId}_${courseId}`,
          JSON.stringify(updated),
        );
        return updated;
      });

      setOutput("✅ Submit Successfully");
      setOutputs((prev) => {
        const updated = {
          ...prev,
          [currentIndex]: "✅ Submit Successfully",
        };

        localStorage.setItem(
          `outputs_${userId}_${courseId}`,
          JSON.stringify(updated),
        );
        return updated;
      });
    } catch (err) {
      setOutput("❌ Submit failed");
    }
  };

  // ------------------------------------------------------------------

  const Divider = () => (
    <hr
      style={{
        border: "none",
        height: "1px",
        background: "linear-gradient(to right, #6366f1, #22c55e)",
        margin: "24px 0",
      }}
    />
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ------------------new useeefct-------------------
  useEffect(() => {
    const testName = localStorage.getItem(`testName_${userId}`);

    const isCompleted = localStorage.getItem(`completedTest_${testName}`);
    const wasEliminated = localStorage.getItem("quiz_eliminated");

    if (mode === "CUSTOM") return;

    if (isCompleted === "true" || wasEliminated === "true") {
      console.log("🚫 Old test detected → forcing fresh");

      fullResetQuiz();

      localStorage.removeItem("quiz_eliminated");
      localStorage.removeItem(`codingSubmitted_${userId}_${courseId}`);
    }
  }, []);
  // ------------------new useeefct-------------------

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // -------------------submit all question---------------
  const submitAllAnswer = async (redirectTo = null) => {
    try {
      const token = localStorage.getItem("token");

      if (!questions.length) {
        toast.error("No questions found!");
        return;
      }

      const testName =
        localStorage.getItem(`testName_${userId}`) || questions[0]?.testName;

      const payload = {
        answers: Object.keys(answers).map((index) => ({
          questionId: questions[index]?.questionId,
          answer: answers[index],
        })),
      };

      const response = await axiosClient.post("/auth/finish-test", payload, {
        params: { testName }, // 🔥 IMPORTANT FIX
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      localStorage.setItem("testCompleted", "true");

      console.log("✅ Submit Response:", response.data);

      // ✅ -----------------SINGLE SOURCE OF TRUTH

      if (!testName) {
        toast.error("Test name not found!");
        return;
      }

      // ✅ -----------------IMPORTANT LOCAL STORAGE FLAGS
      localStorage.removeItem(`activeTest_${userId}`);
      localStorage.removeItem(`last_user_${courseId}`);

      // ✅ ----------------Reset stats after test finish
      localStorage.removeItem(CODING_KEY);
      localStorage.removeItem(MCQ_KEY);

      localStorage.setItem(`completedTest_${testName}`, "true");
      localStorage.setItem(`lastTest_${courseId}`, testName);
      localStorage.setItem("lastTestName", testName);

      // ✅------------------- MULTIPLE TEST SAVE
      const old = JSON.parse(localStorage.getItem("completedTests") || "[]");

      const updated = [...new Set([...old, testName])];

      localStorage.setItem("completedTests", JSON.stringify(updated));

      console.log("🧪 Final TestName:", testName);

      // 🔥--------------- Course completed flag
      localStorage.setItem(`courseCompleted_${userId}_${courseId}`, "true");

      // 🔥------------------- Reset UI state
      setQuestionStatus({});
      setCurrentIndex(0);
      setCodingStats({ attempted: 0, skipped: 0 });
      setMcqStats({ attempted: 0, skipped: 0 });

      // ✅ ----------------SweetAlert + Navigate

      // 🔴 ------------TEST COMPLETED MARK
      localStorage.setItem(`completedTest_${testName}`, "true");

      // 🔴 -----------------COURSE KA LAST TEST SAVEc
      localStorage.setItem(`lastTest_${courseId}`, testName);

      // 🔔 --------------------dashboard ko update batao
      window.dispatchEvent(new Event("resume-status-update"));

      const backendMsg =
        response?.data?.message || "Test finished successfully";

      await Swal.fire({
        icon: "success",
        title: backendMsg,
        confirmButtonText: redirectTo ? "OK" : "View Review",
      });

      fullResetQuiz();

      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate(`/review/${encodeURIComponent(testName)}`);
      }

      // ✅ ---------------Auto navigate safety
      /*
       setTimeout(
         () => {
           Swal.close();
           navigate(`/review/${encodeURIComponent(testName)}`);
         },
         2 * 60 * 1000,
       );
       */
    } catch (error) {
      console.error("❌ Submit Error:", error);

      Swal.fire({
        icon: "error",
        title: "Submission Failed ❌",
        text: error?.response?.data?.message || "Something went wrong!",
      });
    }
  };

  // -----------------Select Option---------------

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axiosClient.get("/auth/api/users/compiler/languages");

        const apiLanguages = res.data || [];

        const priorityOrder = ["python", "c", "c++", "java", "javascript"];
        const getName = (lang) => (lang.name || lang.label || "").toLowerCase();

        const priorityLangs = [];
        const otherLangs = [];

        apiLanguages.forEach((lang) => {
          const name = getName(lang);
          if (priorityOrder.some((p) => name.includes(p))) {
            priorityLangs.push(lang);
          } else {
            otherLangs.push(lang);
          }
        });

        priorityLangs.sort((a, b) => {
          const aName = getName(a);
          const bName = getName(b);
          return (
            priorityOrder.findIndex((p) => aName.includes(p)) -
            priorityOrder.findIndex((p) => bName.includes(p))
          );
        });

        const finalLanguages = [...priorityLangs, ...otherLangs];
        setLanguages(finalLanguages);

        const defaultLang = priorityLangs[0] || finalLanguages[0];
        if (defaultLang) setSelectedLang(defaultLang);
      } catch (err) {
        console.error("❌ Language fetch failed", err?.response || err);
        toast.error("Language API failed");
      }
    };

    fetchLanguages();
  }, []);

  // ---------------Compilor-------------
  const submitCodeToCompiler = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!code?.trim()) {
        toast.error("Please write some code!");
        return;
      }

      // 🔥 Start Loader
      setIsRunning(true);

      // Clear previous output
      setOutput("");

      const payload = {
        source_code: code,
        stdin: stdin || "",
        language_id: selectedLang?.id || 71,
      };

      const res = await axiosClient.post(
        "/auth/api/users/compiler/run",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("✅ Compiler response:", res.data);

      let outputText = "";

      if (typeof res.data === "string") {
        outputText = res.data; // 👈 THIS FIXES YOUR ISSUE
      } else {
        outputText =
          res.data?.stdout ||
          res.data?.output ||
          res.data?.compile_output ||
          res.data?.stderr ||
          JSON.stringify(res.data, null, 2);
      }

      const finalOutput = outputText || "⚠️ No output received";

      // ✅ -------------Screen par show
      setOutput(finalOutput);

      // ✅ ONLY FILTER MODE me save karo
      if (mode === "CUSTOM") {
        setOutputs((prev) => {
          const updated = {
            ...prev,
            [currentIndex]: finalOutput,
          };

          localStorage.setItem(
            `outputs_${userId}_${courseId}`,
            JSON.stringify(updated),
          );

          return updated;
        });
      }
      toast.success("Code Executed Successfully 🚀");

      // 🔥 Scroll to output AFTER output is rendered
      setTimeout(() => {
        outputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (err) {
      console.error("❌ Compiler API Error:", err?.response || err);
      setOutput("❌ Compiler Error. Check console.");
      toast.error("Code execution failed");
      // 🔥 Scroll to error output too
      setTimeout(() => {
        outputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } finally {
      // 🔥 Stop Loader
      setIsRunning(false);
    }
  };

  // --------------Selection Radio---------------
  // ✅ PERSIST STATS KEYS
  const CODING_KEY = `codingStats_${userId}_${courseId}`;
  const MCQ_KEY = `mcqStats_${userId}_${courseId}`;

  // ✅ Stats are derived from questionStatus (single source of truth)
  const [codingStats, setCodingStats] = useState({ attempted: 0, skipped: 0 });
  const [mcqStats, setMcqStats] = useState({ attempted: 0, skipped: 0 });

  useEffect(() => {
    if (!questions.length) return;

    const c = { attempted: 0, skipped: 0 };
    const m = { attempted: 0, skipped: 0 };

    questions.forEach((qq, i) => {
      const st = questionStatus[i];
      if (!st) return;

      const opts = [qq.optionA, qq.optionB, qq.optionC, qq.optionD].filter(
        Boolean,
      );
      const isMcq =
        qq.formate === "MCQ" || qq.format === "MCQ" || opts.length > 0;
      const bucket = isMcq ? m : c;

      if (st === "DONE") bucket.attempted += 1;
      else if (st === "SKIP") bucket.skipped += 1;
    });

    setCodingStats(c);
    setMcqStats(m);
  }, [questionStatus, questions]);

  // ✅ Auto Save Coding Stats
  useEffect(() => {
    localStorage.setItem(CODING_KEY, JSON.stringify(codingStats));
  }, [codingStats]);

  // ✅ Auto Save MCQ Stats
  useEffect(() => {
    localStorage.setItem(MCQ_KEY, JSON.stringify(mcqStats));
  }, [mcqStats]);

  // -------------------------------------------------------------------------------

  useEffect(() => {
    const savedAnswers =
      JSON.parse(localStorage.getItem(`answers_${userId}_${courseId}`)) || {};

    // setAnswers(savedAnswers);
  }, [userId, courseId]);

  useEffect(() => {
    setTestResults(null);
    setTestMode(null);
  }, [currentIndex]);

  useEffect(() => {
    if (mode !== "CUSTOM") return;

    const savedCodes =
      JSON.parse(localStorage.getItem(`codes_${userId}_${courseId}`)) || {};

    setCodes(savedCodes);
    setCode(savedCodes[currentIndex] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, courseId]);

  // useEffect(() => {
  //   const savedOutputs =
  //     JSON.parse(localStorage.getItem(`outputs_${userId}_${courseId}`)) || {};

  //   setOutputs(savedOutputs);
  // }, [userId, courseId]);

  useEffect(() => {
    const testName = questions?.[0]?.testName;

    if (!testName) return;

    const isCompleted = localStorage.getItem(`completedTest_${testName}`);

    if (isCompleted === "true") {
      console.log("🧹 Clearing old outputs after test complete");

      setOutput("");
      setOutputs({});
    }
  }, [questions]);

  // --------------------------------------------------

  useEffect(() => {
    const lastUser = localStorage.getItem("last_user_global");

    // reset only if a DIFFERENT user was stored before
    if (lastUser && lastUser !== userId) {
      console.log("🆕 New User Detected → Full Reset");

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("status_") ||
          key.startsWith("codingStats_") ||
          key.startsWith("mcqStats_") ||
          key.startsWith("answers_") ||
          key.startsWith("codes_") ||
          key.startsWith("outputs_")
        ) {
          localStorage.removeItem(key);
        }
      });

      setQuestionStatus({});
      setCodes({});
      setAnswers({});
      setOutputs({});
      setCurrentIndex(0);
      setCodingStats({ attempted: 0, skipped: 0 });
      setMcqStats({ attempted: 0, skipped: 0 });
    }

    localStorage.setItem("last_user_global", userId);
  }, [userId]);

  useEffect(() => {
    const lastCourse = localStorage.getItem(`lastCourse_${userId}`);

    if (lastCourse && lastCourse !== courseId) {
      console.log("📘 New Course → Fresh Start");

      localStorage.removeItem(`status_${userId}_${courseId}`);
      localStorage.removeItem(`codingStats_${userId}_${courseId}`);
      localStorage.removeItem(`mcqStats_${userId}_${courseId}`);
      localStorage.removeItem(`answers_${userId}_${courseId}`);
      localStorage.removeItem(`codes_${userId}_${courseId}`);
      // localStorage.removeItem(`outputs_${userId}_${courseId}`);

      // 🔥 RESET UI STATE
      setOutputs({});
      setAnswers({});
      setCodes({});
      setQuestionStatus({});
      setOutput(""); // ⭐ MOST IMPORTANT (ye hi issue fix karega)
      setQuestionStatus({});
      setAnswers({});
      setCodes({});
      setOutputs({});
      setCurrentIndex(0);
      setCodingStats({ attempted: 0, skipped: 0 });
      setMcqStats({ attempted: 0, skipped: 0 });
    }

    localStorage.setItem(`lastCourse_${userId}`, courseId);
  }, [courseId, userId]);

  // 🔥 ADD THIS HERE
  // if (checkingProfile) return null;

  const handleExitTest = async () => {
    setUserMenuOpen(false);

    // No questions loaded → nothing to submit, just leave
    if (!questions.length) {
      if (window.stopCamera) window.stopCamera();
      fullResetQuiz();
      navigate("/front");
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Exit Test?",
      text: "Your test will be submitted now and you can't change your answers after this.",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    if (window.stopCamera) window.stopCamera();

    // 1) save the answer on the current question
    try {
      const userAnswer = isMCQ ? answers[currentIndex] : code?.trim();
      await submitCurrentAnswerDirect(userAnswer || null);
    } catch (err) {
      console.log("⚠️ Could not save last answer before exit:", err);
    }

    // 2) finish-test, then go to /front
    await submitAllAnswer("/front");
  };

  if (loading) {
    return (
      <div style={s.center}>
        <style>{`
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
          .loading-img { animation: bounce 1.5s infinite ease-in-out; width: 120px; }
        `}</style>
        <div style={{ textAlign: "center" }}>
          <img src="Zesty Hii1.png" alt="Loading..." className="loading-img" />
          <p style={{ marginTop: 20, fontWeight: "bold", color: "#6366f1" }}>
            Loading Zesty quiz...
          </p>
        </div>
      </div>
    );
  }

  const q = questions?.[currentIndex] ?? null;
  // const options = [q.optionA, q.optionB, q.optionC, q.optionD].filter(Boolean);
  const options = q
    ? [q.optionA, q.optionB, q.optionC, q.optionD].filter(Boolean)
    : [];
  // ✅ MCQ detect (format OR options)
  const isMCQ =
    q?.formate === "MCQ" || q?.format === "MCQ" || options.length > 0;

  const runCode = () => {
    setOutput(
      "> Code executed successfully ✔\n" +
        (Math.random() > 0.5
          ? "Output: Success"
          : "Output: Traceback error..."),
    );
  };

  const rawText =
    q?.description ||
    q?.questionText ||
    q?.question ||
    "Question not available";

  const parts = rawText.split(/(?=\s\d+[\)\.]\s)/);

  return (
    <div style={s.page}>
      {/*-------------- Scrollable Container------------- */}
      <div style={s.scrollContainer}>
        {/* 🔝---------------- USER PROFILE BAR--------- */}
       {/* 🔝---------------- USER PROFILE BAR--------- */}
<div
  style={{
    ...s.userBar,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  }}
>
  <div style={{ ...s.userBox, cursor: "default" }}>
    <div style={s.avatar}>{userName?.charAt(0)?.toUpperCase()}</div>

    <div style={s.userInfo}>
      <span style={s.userName}>{userName}</span>
      <span style={s.userEmail}>{userEmail}</span>
    </div>
  </div>

  <button
    type="button"
    onClick={handleExitTest}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "9px 16px",
      borderRadius: 8,
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#dc2626",
      fontWeight: 700,
      fontSize: 14,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "#dc2626";
      e.currentTarget.style.color = "#fff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "#fef2f2";
      e.currentTarget.style.color = "#dc2626";
    }}
  >
    🚪 Exit Test
  </button>
</div>

        <div
          style={{
            ...s.content,
            flexDirection:
              terminalMode === "right" && !isMCQ ? "row" : "column",
          }}
        >
          {/* --------------QUESTION CARD------------------- */}

          <div
            ref={questionRef}
            style={{
              ...s.questionCard,
              width: terminalMode === "right" && !isMCQ ? "55%" : "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {questions.map((_, index) => {
                let bg = "#ffffff"; // default white

                if (questionStatus[index] === "DONE") {
                  bg = "#16a34a"; // green
                } else if (questionStatus[index] === "SKIP") {
                  bg = "#facc15"; // yellow
                }

                return (
                  <div
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: bg,
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </div>

            {/* -------------------META INFO INSIDE QUESTION CARD */}
            <div style={s.metaRow}>
              {/*-------------- LEFT → TOPIC CHIPS */}
              <div
                style={{
                  ...s.infoBar,
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 12,
                }}
              >
                {/* --------------------QUESTION NAVIGATOR */}
                <div style={s.qNavSingleBox}>
                  <span style={s.qNavPrev}>
                    ⬅ {currentIndex > 0 ? currentIndex : "-"}
                  </span>

                  <span style={s.qNavCurrent}>{currentIndex + 1}</span>

                  <span style={s.qNavNext}>
                    {currentIndex + 2 <= questions.length
                      ? currentIndex + 2
                      : "-"}{" "}
                    ➡
                  </span>
                </div>

                {/*---------------- MODULE ---------------*/}
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Module</span>
                  <span
                    style={{
                      ...s.infoChip,
                      background: "#e0f2fe",
                      color: "#0369a1",
                    }}
                  >
                    {q?.module || moduleName || "-"}
                  </span>
                </div>

                {/*-------------- DIFFICULTY------------------- */}
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>Difficulty</span>
                  <span
                    style={{
                      ...s.infoChip,
                      background: "#dcfce7",
                      color: "#166534",
                    }}
                  >
                    {q?.difficultyLevel || "-"}
                  </span>
                </div>
              </div>

              {/* ------------------------------------jobRoleCompetency----------- */}

              <div style={s.infoItem}>
                <span style={s.infoLabel}>Role</span>

                <span
                  style={{
                    ...s.infoChip,
                    background: "#fef3c7",
                    color: "#92400e",
                  }}
                >
                  {q?.jobRoleCompetency ?? "-"}
                </span>
              </div>
              {/* ------------------------------------jobRoleCompetency----------- */}

              {/* ---------------companyAsked------------------ */}

              <div style={s.infoItem}>
                <span style={s.infoLabel}>Companies</span>

                <span
                  style={{
                    ...s.infoChip,
                    background: "#ede9fe",
                    color: "#5b21b6",
                  }}
                >
                  {q?.companyAsked ?? "-"}
                </span>
              </div>
              {/* ---------------companyAsked------------------ */}

              {/*------------- RIGHT → STATUS BOXES----------- */}
              <div
                style={{
                  ...s.statusRow,
                  flexWrap: isMobile ? "nowrap" : "wrap",
                }}
              >
                {/*------------- 💻 CODING----------------- */}
                <div style={s.statusBox}>
                  <h4 style={s.statusTitle}>💻 Coding</h4>

                  {/*---- Attempted -----*/}
                  <div style={s.radioRow}>
                    <span
                      style={{
                        ...s.dot,
                        background:
                          codingStats.attempted > 0 ? "#16a34a" : "#dc2626",
                      }}
                    />
                    <span>{codingStats.attempted} Attempted</span>
                  </div>

                  {/*-------- Skipped-------- */}
                  <div style={s.radioRow}>
                    <span
                      style={{
                        ...s.dot,
                        background:
                          codingStats.skipped > 0 ? "#dc2626" : "#16a34a",
                      }}
                    />
                    <span>{codingStats.skipped} Skipped</span>
                  </div>
                </div>

                {/*--------- 📝 MCQ -------------*/}
                <div
                  style={{
                    ...s.statusBox,
                    minWidth: isMobile ? 110 : 140,
                    padding: isMobile ? "8px 10px" : "10px 14px",
                  }}
                >
                  <h4 style={s.statusTitle}>📝 MCQ</h4>

                  {/*------ Attempted---------- */}
                  <div style={s.radioRow}>
                    <span
                      style={{
                        ...s.dot,
                        background:
                          mcqStats.attempted > 0 ? "#16a34a" : "#dc2626",
                      }}
                    />
                    <span>{mcqStats.attempted} Attempted</span>
                  </div>

                  {/*------------Skipped------------- */}
                  <div style={s.radioRow}>
                    <span
                      style={{
                        ...s.dot,
                        background:
                          mcqStats.skipped > 0 ? "#dc2626" : "#16a34a",
                      }}
                    />
                    <span>{mcqStats.skipped} Skipped</span>
                  </div>
                </div>
              </div>
            </div>

            {/* -----------------Timer--------------- */}
            <br></br>

            {/*Background Image */}

            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>
              ⏱ Time: {timeTaken}s
            </div>

            {/* -----------------Timer--------------- */}
            {/* ----------✅ QUESTION TITLE-------- */}
            <h2 style={{ color: "#1e293b", marginTop: 12 }}>
              Question {currentIndex + 1}
            </h2>
            <div style={{ position: "relative" }}>
              {/* ✅ WATERMARK LOGO */}
              <img
                src={codematrixLogo}
                alt="CodeMatrix"
                style={{
                  position: "absolute",
                  top: "120%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",

                  maxWidth: "70%", // 🔥 responsive
                  height: "auto", // 🔥 no cut

                  opacity: 0.06,
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />

              {/* Content */}
              <div style={{ position: "relative", zIndex: 2 }}>
                {/* <p
                  style={{
                    fontSize: "15px",
                    lineHeight: "1.6",
                    color: "#334155",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                  }}
                  onCopy={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {q?.description ||
                    q?.questionText ||
                    q?.question ||
                    "Question not available"}
                </p> */}

                <div
                  style={{
                    fontSize: "15px",
                    lineHeight: "1.7",
                    color: "#334155",
                  }}
                >
                  {parts.map((part, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: "10px",
                        paddingLeft: /^\d+[\.\)]/.test(part) ? "20px" : "0px",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                        /*For Firefox */
                        msUserSelect: "none",
                      }}
                    >
                      {part.trim()}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {isMCQ && (
              <div style={{ marginTop: 20 }}>
                {options.map((opt, index) => {
                  const letter = ["A", "B", "C", "D"][index];

                  return (
                    <div
                      key={index}
                      onClick={async () => {
                        // allow change answer
                        const prevStatus = questionStatus[currentIndex];
                        // setOutput(""); // 🔥 old message clear
                        // 1️⃣ ---------Local state update
                        const newAnswers = {
                          ...answers,
                          [currentIndex]: letter,
                        };
                        setAnswers(newAnswers);

                        // --------------SAVE TO LOCALSTORAGE
                        localStorage.setItem(
                          `answers_${userId}_${courseId}`,
                          JSON.stringify(newAnswers),
                        );

                        // 2️⃣ Force UI update before API
                        // setOutput("Submitting...");

                        // --------Direct submit with selected value
                        //   await submitCurrentAnswerDirect(letter);
                      }}
                      style={{
                        ...s.option,
                        background:
                          answers[currentIndex] === letter ? "#e0e7ff" : "#fff",
                        borderColor:
                          answers[currentIndex] === letter
                            ? "#6366f1"
                            : "#c7d2fe",
                      }}
                    >
                      <CheckCircle
                        size={18}
                        color={
                          answers[currentIndex] === letter
                            ? "#6366f1"
                            : "#94a3b8"
                        }
                      />

                      {/*------------ UI me letter + option text */}
                      <strong style={{ marginRight: 8 }}>{letter}.</strong>
                      {/* Make option text uncopyable */}
                      <span
                        style={{
                          userSelect: "none", // ❌ Prevent selection
                          WebkitUserSelect: "none",
                          MozUserSelect: "none",
                          /*For Firefox */
                          msUserSelect: "none",
                        }}
                        onCopy={(e) => e.preventDefault()} // ❌ Disable Ctrl+C / Cmd+C
                        onContextMenu={(e) => e.preventDefault()} // ❌ Disable right-click menu
                      >
                        {opt}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {!isMCQ && q?.constraints && (
              <>
                <Divider />
                <div style={s.detailItem}>
                  <h4 style={s.detailTitle}>⚙️ Constraints</h4>
                  <pre
                    style={{
                      ...s.codeBlock,
                      userSelect: "none", // ❌ Prevent selecting the text
                      WebkitUserSelect: "none", // ❌ Safari/Chrome
                      MozUserSelect: "none", // ❌ Firefox
                      msUserSelect: "none", // ❌ IE/Edge
                    }}
                    onCopy={(e) => e.preventDefault()} // ❌ Disable Ctrl+C / Cmd+C copy
                    onContextMenu={(e) => e.preventDefault()} // ❌ Disable right-click menu
                  >
                    {typeof q.constraints === "string"
                      ? q.constraints
                      : safeArrayText(q.constraints).join("\n")}
                  </pre>
                </div>
              </>
            )}

            {!isMCQ && safeArrayText(q?.hints).length > 0 && (
              <>
                <Divider />
                <div style={s.detailItem}>
                  <h4 style={s.detailTitle}>💡 Hint</h4>
                  <pre
                    style={{
                      ...s.codeBlock,
                      userSelect: "none", // ❌ Prevent text selection
                      WebkitUserSelect: "none",
                      MozUserSelect: "none",
                      msUserSelect: "none",
                    }}
                    onCopy={(e) => e.preventDefault()} // ❌ Disable Ctrl+C / Cmd+C copy
                    onContextMenu={(e) => e.preventDefault()} // ❌ Disable right-click menu
                  >
                    {safeArrayText(q.hints).join("\n")}
                  </pre>
                </div>
              </>
            )}

            {!isMCQ && parseExamples(q?.examplesJson).length > 0 && (
              <>
                <Divider />

                <div style={s.detailItem}>
                  <h4 style={s.detailTitle}>📘 Example</h4>

                  {parseExamples(q.examplesJson).map((ex, i) => (
                    <pre
                      key={i}
                      style={{
                        ...s.codeBlock,
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        MozUserSelect: "none",
                        msUserSelect: "none",
                      }}
                    >
                      Input:
                      {ex.input}
                      Output:
                      {ex.output}
                      Explanation:
                      {ex.explanation || ""}
                    </pre>
                  ))}
                </div>
              </>
            )}

            {!isMCQ && (
              <div style={s.detailBox}>
                {q?.datasetSample?.trim() && (
                  <div style={s.detailItem}>
                    <h4 style={s.detailTitle}>📦 Sample Dataset</h4>
                    <pre style={s.codeBlock}>{q.datasetSample}</pre>
                  </div>
                )}

                <Divider />

                {q?.sampleInput?.trim() && (
                  <div style={s.detailItem}>
                    <h4 style={s.detailTitle}>🧾 Sample Input</h4>
                    <pre style={s.codeBlock}>{q.sampleInput}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
          {/*---------- TERMINAL-------------- */}
          {/*-----------TERMINAL OR MCQ OUTPUT -------------*/}
          {!isMCQ ? (
            <div
              style={{
                ...s.terminalCard,
                ...(terminalMode === "right" && s.termRight),
                ...(terminalMode === "full" && s.termFull),
              }}
            >
              {/* 📌------------------- Instruction Box ----------*/}
              <div style={s.instructionBox}>
                <strong>📌 Instructions</strong>
                <ul style={{ marginTop: 2, paddingLeft: 18 }}>
                  <li>
                    Execute code only through a method call; running functions
                    or classes directly will cause errors.
                  </li>
                </ul>
              </div>
              <br></br>
              {/* ---------------- EXISTING TERMINAL CONTENT SAME RAHEGA ---------------- */}
              <div style={s.termControls}>
                <button
                  style={s.iconBtn}
                  onClick={() => setTerminalMode("bottom")}
                >
                  <ArrowDown size={16} />
                </button>

                {/* <button
                  style={s.iconBtn}
                  onClick={() => {
                    requestAnimationFrame(() => {
                      setTerminalMode("right");
                    });
                  }}
                >
                  <ArrowRight size={16} />
                </button> */}

                <button
                  style={s.iconBtn}
                  onClick={() =>
                    setTerminalMode(terminalMode === "full" ? "bottom" : "full")
                  }
                >
                  {terminalMode === "full" ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </button>

                {/* --------Language Dropdown------------ */}

                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setLangOpen(!langOpen)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      background: "#111",
                      color: "#fff",
                      fontSize: "13px",
                      cursor: "pointer",
                      minWidth: "110px",
                      textAlign: "left",
                    }}
                  >
                    {selectedLang?.name || "Select Lang"} ▼
                  </button>

                  {langOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "110%",
                        left: 0,
                        background: "#1e1e1e",
                        border: "1px solid #333",
                        borderRadius: "6px",
                        width: "100%",
                        zIndex: 1000,
                        maxHeight: "180px",
                        overflowY: "auto",
                      }}
                    >
                      {languages.map((lang, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            setSelectedLang(lang);
                            setLangOpen(false);
                          }}
                          style={{
                            padding: "8px 10px",
                            cursor: "pointer",
                            fontSize: "13px",
                            color: "#fff",
                            borderBottom: "1px solid #333",
                          }}
                        >
                          {lang.label || lang.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ----------------editor size------------------- */}
                {/* 🔥 FONT SIZE CONTROLS */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#111",
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #333",
                  }}
                >
                  <button
                    onClick={() =>
                      setEditorFontSize((prev) => Math.max(prev - 2, 10))
                    }
                    style={{
                      background: "#1e293b",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    -
                  </button>

                  <span
                    style={{
                      color: "#fff",
                      fontSize: 13,
                      minWidth: 45,
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    {editorFontSize}px
                  </span>

                  <button
                    onClick={() =>
                      setEditorFontSize((prev) => Math.min(prev + 2, 40))
                    }
                    style={{
                      background: "#1e293b",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    +
                  </button>
                </div>
                {/* For run the code  */}
                <button
                  onClick={submitCodeToCompiler}
                  disabled={isRunning}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                    padding: "7px 14px",
                    border: "none",
                    borderRadius: 6,
                    background: isRunning ? "#64748b" : "#16a34a",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: isRunning ? "not-allowed" : "pointer",
                    minWidth: 110,
                    opacity: isRunning ? 0.8 : 1,
                  }}
                >
                  {isRunning ? (
                    <>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          border: "2px solid rgba(255,255,255,0.4)",
                          borderTop: "2px solid #fff",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "spin 0.7s linear infinite",
                        }}
                      />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play size={16} />
                      Run Code
                    </>
                  )}
                </button>
              </div>
              {/* ----------------editor size------------------- */}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                {/* <span style={{ color: "#94a3b8", fontSize: 13 }}>
                  ⚡ Write code → Run → Submit
                </span> */}

                <div style={{ display: "flex", gap: 10 }}>
                  {/* <button
                    style={{
                      ...s.runBtn,
                      background: "#0c674a",
                      fontWeight: 500,
                      fontSize: "16px",
                      padding: "6px 14px",
                      borderRadius: "6px",
                    }}
                    onClick={submitCodeToCompiler}
                  >
                    ▶ Run
                  </button> */}
                  {/*
                  <button
                    onClick={submitCurrentAnswer}
                    style={{
                      position: "relative",
                      padding: "10px 22px",
                      fontSize: "15px",
                      fontWeight: "600",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      color: "#fff",

                      // 🔥 Gradient Background
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",

                      // 🔥 Glow Effect
                      boxShadow: "0 6px 20px rgba(99,102,241,0.5)",

                      transition: "all 0.3s ease",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px) scale(1.05)";
                      e.target.style.boxShadow = "0 10px 30px rgba(139,92,246,0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0) scale(1)";
                      e.target.style.boxShadow = "0 6px 20px rgba(99,102,241,0.5)";
                    }}
                  >
                    🚀 Submit your code  Answer
                  </button> */}
                </div>
              </div>

              {/* <div style={s.outputContainer}>
             
                <pre style={s.output}>
                  {mode === "CUSTOM"
                    ? safeText(output || "⚡ Run your code")
                    : ""}
                </pre>
              </div> */}

              {/* {mode === "CUSTOM" && codingSubmitted[currentIndex] && ( */}

              <Editor
                key={terminalMode}
                height={isMobile ? "250px" : "400px"}
                language={normalizeLang(selectedLang?.name)}
                theme="vs-dark"
                value={code}
                onChange={(value) => {
                  const newCode = value || "";
                  const prevCode = prevCodeRef.current;

                  // 🔥 Detect typing difference
                  const diff = newCode.length - prevCode.length;

                  if (diff > 0) {
                    // Characters added
                    setTotalKeystrokes((prev) => prev + diff);

                    if (!firstKeyPressTimeRef.current) {
                      firstKeyPressTimeRef.current = Date.now();
                      setInitialThinkingTimeMs(
                        Date.now() - questionStartTimeRef.current,
                      );
                    }
                  }

                  if (diff < 0) {
                    // Characters deleted (Backspace)
                    setBackspaceCount((prev) => prev + Math.abs(diff));
                    setTotalKeystrokes((prev) => prev + Math.abs(diff));
                  }

                  prevCodeRef.current = newCode;

                  setCode(newCode);

                  setCodes((prev) => {
                    const updated = {
                      ...prev,
                      [currentIndex]: newCode,
                    };

                    localStorage.setItem(
                      `codes_${userId}_${courseId}`,
                      JSON.stringify(updated),
                    );

                    return updated;
                  });
                }}
                options={{
                  fontSize: editorFontSize,
                  minimap: { enabled: false },
                  automaticLayout: true,
                  wordWrap: "on",
                  scrollBeyondLastLine: false,
                }}
              />

              {/* Input the Number or Text or String  */}

              <div
                style={{
                  marginTop: 10,
                  background: "#020617",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  Input
                </div>

                <textarea
                  value={stdin}
                  onChange={(e) => setStdin(e.target.value)}
                  placeholder="Enter input here..."
                  style={{
                    width: "100%",
                    minHeight: 80,
                    background: "#0f172a",
                    color: "#e2e8f0",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    padding: 10,
                    resize: "vertical",
                    fontFamily: "monospace",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Show the output of the Student */}

              <div
                ref={outputRef}
                style={{
                  marginTop: 10,
                  background: "#020617",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  Output
                </div>

                <pre
                  style={{
                    margin: 0,
                    minHeight: 100,
                    maxHeight: 250,
                    overflow: "auto",
                    background: "#0f172a",
                    color: "#22c55e",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    padding: 10,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                  }}
                >
                  {output || "Run your code to see the output..."}
                </pre>
              </div>

              {/* Automated test-case results */}
              {testResults && (
                <div
                  ref={testResultsRef}
                  style={{
                    marginTop: 14,
                    padding: 14,
                    background: "#0f172a",
                    color: "#e2e8f0",
                    border: "1px solid #334155",
                    borderRadius: 10,
                  }}
                >
                  <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>
                    {testMode === "SUBMIT"
                      ? "All Test Cases"
                      : "Sample Test Results"}
                  </h3>

                  {testResults.error ? (
                    <div style={{ color: "#f87171" }}>{testResults.error}</div>
                  ) : (
                    <>
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          flexWrap: "wrap",
                          marginBottom: 14,
                        }}
                      >
                        <span style={{ color: "#94a3b8" }}>
                          Total:{" "}
                          {testResults?.totalTestCases ??
                            testResults?.results?.length ??
                            0}
                        </span>

                        <span style={{ color: "#4ade80" }}>
                          Passed: {testResults?.passedTestCases ?? 0}
                        </span>

                        <span style={{ color: "#f87171" }}>
                          Failed: {testResults?.failedTestCases ?? 0}
                        </span>
                      </div>

                      {(testResults.results ?? []).map((test, index) => (
                        <div
                          key={test.testCaseNumber ?? index}
                          style={{
                            marginBottom: 10,
                            padding: 12,
                            borderRadius: 8,
                            border: `1px solid ${
                              test.passed ? "#166534" : "#991b1b"
                            }`,
                            background: test.passed ? "#052e16" : "#450a0a",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <strong>
                              Test Case {test.testCaseNumber ?? index + 1}
                            </strong>

                            <span
                              style={{
                                color: test.passed ? "#4ade80" : "#fca5a5",
                                fontWeight: 700,
                              }}
                            >
                              {test.passed ? "✓ Passed" : "✗ Failed"}
                            </span>
                          </div>

                          <div style={{ marginTop: 8, fontSize: 13 }}>
                            Status:{" "}
                            {test.status ||
                              (test.passed ? "Accepted" : "Failed")}
                          </div>

                          {test.executionTime != null && (
                            <div style={{ marginTop: 5, fontSize: 13 }}>
                              Execution time: {test.executionTime}
                              {typeof test.executionTime === "number" ||
                              /^\d+(\.\d+)?$/.test(String(test.executionTime))
                                ? " s"
                                : ""}
                            </div>
                          )}

                          {test.input != null && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                Input
                              </div>
                              <pre style={testOutputStyle}>
                                {test.input || "(empty input)"}
                              </pre>
                            </div>
                          )}

                          {test.expectedOutput != null && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                Expected Output
                              </div>
                              <pre style={testOutputStyle}>
                                {test.expectedOutput || "(empty output)"}
                              </pre>
                            </div>
                          )}

                          {test.actualOutput != null && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                Your Output
                              </div>
                              <pre style={testOutputStyle}>
                                {test.actualOutput || "(empty output)"}
                              </pre>
                            </div>
                          )}

                          {test.message && (
                            <div style={{ marginTop: 8, color: "#fca5a5" }}>
                              {test.message}
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <button
                  style={{ ...s.runBtn, background: "#0c674a" }}
                  onClick={submitCodeToCompiler}
                >
                  <Send size={16} /> Run
                </button>

                <button
                  style={{ ...s.runBtn, background: "#6366f1" }}
                  onClick={submitCurrentAnswer}
                >
                  <Send size={16} /> Submit
                </button>
              </div> */}
              {/* <div style={s.outputContainer}>
                <pre style={s.output}>{safeText(output)}</pre>
              </div> */}
            </div>
          ) : (
            // ✅ MCQ OUTPUT UI
            <div
              style={{
                background: "#0f172a",
                borderRadius: 14,
                padding: 20,
                marginTop: 20,
                color: "#2dd4bf",
                fontSize: 14,
              }}
            >
              <div
                style={{
                  background: "#0f172a",
                  borderRadius: 14,
                  padding: 20,
                  marginTop: 20,
                  color: "#2dd4bf",
                  fontSize: 14,
                }}
              >
                {output && questionStatus[currentIndex] === "DONE" && (
                  <>
                    <h4>✅ MCQ Submission Output</h4>
                    <pre>{safeText(output)}</pre>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FIXED NAVIGATION BAR - Ye ab move nahi karega */}
      <div style={s.navFixed}>
        <div style={s.navContent}>
          <div style={{ fontWeight: "800", color: "#1e293b" }}>
            {Math.min(currentIndex + 1, questions.length)} / {questions.length}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            {/* 🔥 NEW SUBMIT BUTTON (ADD HERE) */}
            {!isMCQ && (
              <button
                onClick={submitCurrentAnswer}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",

                  padding: "10px 18px",
                  fontSize: "19px",
                  fontWeight: "700",

                  borderRadius: "999px",
                  border: "1px solid #c7d2fe",

                  background: "#f8fafc", // 🔥 light soft background
                  color: "#000", // 🔥 primary text color

                  cursor: "pointer",

                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)", // 🔥 subtle shadow
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#eef2ff";
                  e.currentTarget.style.borderColor = "#6366f1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.borderColor = "#c7d2fe";
                }}
              >
                <span style={{ fontSize: "15px" }}>⬆️</span>
                Submit Code
              </button>
            )}

            {/* ----------PREVIOUS BUTTON---------- */}
            <button
              style={{
                ...s.navBtn,
                opacity: currentIndex === 0 ? 0.4 : 1,
                cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              }}
              disabled={currentIndex === 0}
              onClick={() => {
                if (currentIndex > 0) {
                  setCurrentIndex((prev) => prev - 1);
                }
              }}
            >
              ⬅️ Previous
            </button>

            {/* ----------NEXT / FINISH BUTTON---------- */}
            {/* ----------NEXT / SUBMIT BUTTON--------------- */}
            {currentIndex === questions.length - 1 ? (
              <button
                type="button"
                onClick={async () => {
                  const result = await Swal.fire({
                    icon: "question",
                    title: "Submit Test?",
                    text: "Are you sure you want to submit the test? You can't change your answers after this.",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    confirmButtonColor: "#6366f1",
                    cancelButtonColor: "#94a3b8",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    reverseButtons: true,
                  });

                  // user chose "No" → stay on the question
                  if (!result.isConfirmed) return;

                  // stop the camera only AFTER the user confirms
                  if (window.stopCamera) window.stopCamera();

                  const userAnswer = isMCQ
                    ? answers[currentIndex]
                    : code?.trim();

                  await submitCurrentAnswerDirect(userAnswer || null);

                  submitAllAnswer();
                }}
                style={{
                  ...s.navBtn,
                  background: "#6366f1",
                  color: "#fff",
                  border: "none",
                }}
              >
                <Send size={16} /> Finish
              </button>
            ) : (
              <button
                style={s.navBtn}
                onClick={async () => {
                  if (!questions.length || !currentQuestion?.questionId) {
                    console.log("Question not ready");
                    return;
                  }

                  const userAnswer = isMCQ
                    ? answers[currentIndex]
                    : code?.trim();

                  if (isMCQ) {
                    await submitCurrentAnswerDirect(userAnswer || null);
                  } else if (userAnswer) {
                    await submitCurrentAnswer(); // saves code, marks DONE
                  } else {
                    await submitCurrentAnswerDirect(null); // marks SKIP
                  }

                  // ➜ go to next question
                  setCurrentIndex((prev) => prev + 1);
                  setOutput("");
                  setCode("");
                  resetTerminal();

                  setCodingSubmitted((prev) => {
                    const updated = { ...prev };
                    delete updated[currentIndex];
                    return updated;
                  });
                }}
              >
                Next ➡️
              </button>
            )}
          </div>

          {!isMCQ && (
            <>
              {/* Run sample test cases */}
              <button
                type="button"
                onClick={() => runAutomatedTests("RUN_SAMPLE")}
                disabled={isRunning || isTesting}
                style={{
                  padding: "8px 12px",
                  border: "none",
                  borderRadius: 6,
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: isRunning || isTesting ? "not-allowed" : "pointer",
                  opacity: isRunning || isTesting ? 0.65 : 1,
                }}
              >
                {isTesting && testMode === "RUN_SAMPLE"
                  ? "Testing..."
                  : "Run Sample Tests"}
              </button>

              {/* Run all test cases */}
              <button
                type="button"
                onClick={() => runAutomatedTests("SUBMIT")}
                disabled={isRunning || isTesting}
                style={{
                  padding: "8px 12px",
                  border: "none",
                  borderRadius: 6,
                  background: "#7c3aed",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: isRunning || isTesting ? "not-allowed" : "pointer",
                  opacity: isRunning || isTesting ? 0.65 : 1,
                }}
              >
                {isTesting && testMode === "SUBMIT"
                  ? "Evaluating..."
                  : "Run All Test Cases"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
<style jsx>{`
  * {
    box-sizing: border-box;
  }

  @media (max-width: 768px) {
    .monaco-editor {
      width: 100% !important;
    }

    pre {
      white-space: pre-wrap !important;
      word-break: break-word !important;
    }

    button {
      font-size: 12px !important;
    }
  }
`}</style>;

<style>
  {`
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `}
</style>;
